package com.schoolconnect.app.feature.subjects

import com.schoolconnect.app.datasource.network.request.SubjectCreateRequest
import com.schoolconnect.app.datasource.network.request.SubjectUpdateRequest
import com.schoolconnect.app.datasource.network.response.SubjectResponse
import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.compareBySmartText

internal const val SubjectCodeMaxLength = 10

data class SubjectAnalyticsUi(
    val total: Int,
    val active: Int,
    val inactive: Int
)

data class SubjectFilterCriteria(
    val subjectCode: String = "",
    val subjectName: String = "",
    val category: String = "",
    val status: String = "",
    val createdFrom: String = "",
    val createdTo: String = ""
)

data class SubjectEditorDraft(
    val id: String = "",
    val schoolId: String = "",
    val code: String = "",
    val name: String = "",
    val category: String = "",
    val createdAt: String = "",
    val updatedAt: String = ""
)

data class SubjectEditorValidationErrors(
    val code: String = "",
    val name: String = "",
    val category: String = ""
) {
    val hasAnyError: Boolean get() = code.isNotBlank() || name.isNotBlank() || category.isNotBlank()
}

internal fun validateSubjectEditor(draft: SubjectEditorDraft): SubjectEditorValidationErrors {
    val code = draft.code.trim()
    val name = draft.name.trim()
    return SubjectEditorValidationErrors(
        code = when {
            code.isBlank() -> "Subject code is required."
            code.length > SubjectCodeMaxLength -> "Subject code must be 10 characters or fewer."
            code.any { !it.isLetterOrDigit() || it.isLowerCase() } -> "Use uppercase letters and numbers only."
            else -> ""
        },
        name = if (name.isBlank()) "Subject name is required." else "",
        category = if (draft.category.trim().isBlank()) "Category is required." else ""
    )
}

enum class SubjectStatusUi(val label: String) {
    ACTIVE("Active"),
    INACTIVE("Inactive")
}

enum class SubjectsSortField(val apiField: String) {
    CODE("short_code"),
    NAME("name"),
    CATEGORY("category"),
    STATUS("is_active")
}

enum class SubjectsDialogMode {
    NONE,
    FILTER,
    EDITOR,
    DETAILS,
    DELETE_CONFIRM
}

data class SubjectRowUi(
    val id: String,
    val schoolId: String = "",
    val code: String,
    val name: String,
    val category: String,
    val status: SubjectStatusUi,
    val createdAt: String = "",
    val updatedAt: String = "",
    val createdBy: String = "",
    val updatedBy: String = ""
)

data class SubjectClassAssignmentUi(
    val className: String,
    val sectionName: String
) {
    val displayName: String
        get() = listOf(className, sectionName)
            .filter { it.isNotBlank() }
            .joinToString(" - ")
}

data class SubjectsUiState(
    override val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val errorMessage: String = "",
    val apiErrorMessage: String = "",
    val infoMessage: String = "",
    val searchQuery: String = "",
    val filters: SubjectFilterCriteria = SubjectFilterCriteria(),
    val sortField: SubjectsSortField = SubjectsSortField.CODE,
    val sortAscending: Boolean = true,
    val dialogMode: SubjectsDialogMode = SubjectsDialogMode.NONE,
    val activeSubjectId: String = "",
    val assignedClasses: List<SubjectClassAssignmentUi> = emptyList(),
    val isAssignmentsLoading: Boolean = false,
    val assignmentsErrorMessage: String = "",
    val editorDraft: SubjectEditorDraft = SubjectEditorDraft(),
    val filterDraft: SubjectFilterCriteria = SubjectFilterCriteria(),
    val categoryOptions: List<String> = emptyList(),
    val subjects: List<SubjectRowUi> = emptyList()
) : ServerPagedListUiState

internal fun subjectFilterLabels(filters: SubjectFilterCriteria): List<String> = buildList {
    if (filters.subjectCode.isNotBlank()) add("Code: ${filters.subjectCode.trim()}")
    if (filters.subjectName.isNotBlank()) add("Name: ${filters.subjectName.trim()}")
    if (filters.category.isNotBlank()) add("Category: ${filters.category.trim()}")
    if (filters.status.isNotBlank()) add("Status: ${filters.status.trim()}")
    if (filters.createdFrom.isNotBlank() || filters.createdTo.isNotBlank()) {
        add("Created: ${filters.createdFrom.ifBlank { "Any" }} to ${filters.createdTo.ifBlank { "Any" }}")
    }
}

sealed interface SubjectsUiEvent {
    data object BackClicked : SubjectsUiEvent
    data class SearchChanged(val query: String) : SubjectsUiEvent
    data class CategoryChanged(val category: String) : SubjectsUiEvent
    data class SortChanged(val field: SubjectsSortField) : SubjectsUiEvent
    data class PageChanged(val pageIndex: Int) : SubjectsUiEvent
    data class PageSizeChanged(val pageSize: Int) : SubjectsUiEvent
    data object FilterClicked : SubjectsUiEvent
    data object FilterDismissed : SubjectsUiEvent
    data class FilterApplied(val filters: SubjectFilterCriteria) : SubjectsUiEvent
    data object FilterReset : SubjectsUiEvent
    data object AddClicked : SubjectsUiEvent
    data class ViewClicked(val subjectId: String) : SubjectsUiEvent
    data class EditClicked(val subjectId: String) : SubjectsUiEvent
    data class DeleteClicked(val subjectId: String) : SubjectsUiEvent
    data object DeleteConfirmed : SubjectsUiEvent
    data object DialogDismissed : SubjectsUiEvent
    data object EditorSaveRequested : SubjectsUiEvent
    data class EditorCodeChanged(val value: String) : SubjectsUiEvent
    data class EditorNameChanged(val value: String) : SubjectsUiEvent
    data class EditorCategoryChanged(val value: String) : SubjectsUiEvent
    data object ClearMessages : SubjectsUiEvent
}

internal fun subjectStatusFromLabel(label: String): SubjectStatusUi = when (label.trim().lowercase()) {
    SubjectStatusUi.INACTIVE.label.lowercase() -> SubjectStatusUi.INACTIVE
    else -> SubjectStatusUi.ACTIVE
}

internal fun subjectCategoryLabel(value: String): String = value.trim().ifBlank { "Other" }

internal fun subjectStatusFromBoolean(isActive: Boolean): SubjectStatusUi =
    if (isActive) SubjectStatusUi.ACTIVE else SubjectStatusUi.INACTIVE

internal fun subjectsComparator(
    field: SubjectsSortField,
    ascending: Boolean
): Comparator<SubjectRowUi> {
    val comparator = when (field) {
        SubjectsSortField.CODE -> compareBySmartText<SubjectRowUi> { it.code }
        SubjectsSortField.NAME -> compareBySmartText<SubjectRowUi> { it.name }
        SubjectsSortField.CATEGORY -> compareBySmartText<SubjectRowUi> { it.category }
        SubjectsSortField.STATUS -> compareBy<SubjectRowUi> { if (it.status == SubjectStatusUi.ACTIVE) 0 else 1 }
    }
    return if (ascending) comparator else comparator.reversed()
}

internal fun SubjectResponse.toRowUi(): SubjectRowUi = SubjectRowUi(
    id = id.orEmpty(),
    schoolId = schoolId.orEmpty(),
    code = shortCode.orEmpty(),
    name = name.orEmpty(),
    category = category.orEmpty().ifBlank { "Other" },
    status = subjectStatusFromBoolean(isActive == true),
    createdAt = createdAt.orEmpty().toDisplayDate(),
    updatedAt = updatedAt.orEmpty().toDisplayDate(),
    // IDs are transport values and must not be rendered as audit names.
    // The detail flow resolves missing names through UserRepository.
    createdBy = createdByName.orEmpty().takeUnless { it.isUserIdLike() }.orEmpty(),
    updatedBy = updatedByName.orEmpty().takeUnless { it.isUserIdLike() }.orEmpty()
)

internal fun String.isUserIdLike(): Boolean {
    val value = trim()
    return value.length == 36 && value[8] == '-' && value[13] == '-' &&
        value[18] == '-' && value[23] == '-' &&
        value.replace("-", "").all { it.isDigit() || it.lowercaseChar() in 'a'..'f' }
}

internal fun SubjectRowUi.toDraft(): SubjectEditorDraft = SubjectEditorDraft(
    id = id,
    schoolId = schoolId,
    code = code,
    name = name,
    category = category,
    createdAt = createdAt,
    updatedAt = updatedAt
)

internal fun SubjectEditorDraft.toCreateRequest(): SubjectCreateRequest =
    SubjectCreateRequest(
        name = name.trim(),
        shortCode = code.trim().uppercase(),
        category = category.trim().takeIf { it.isNotBlank() },
        isActive = true
    )

internal fun SubjectEditorDraft.toUpdateRequest(): SubjectUpdateRequest =
    SubjectUpdateRequest(
        schoolId = schoolId.trim().takeIf { it.isNotBlank() },
        name = name.trim().takeIf { it.isNotBlank() },
        shortCode = code.trim().uppercase().takeIf { it.isNotBlank() },
        category = category.trim().takeIf { it.isNotBlank() },
        isActive = null
    )

private fun String.toDisplayDate(): String {
    val trimmed = trim()
    if (trimmed.isBlank()) return ""
    val datePart = trimmed.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return trimmed
    val parts = datePart.split("-")
    if (parts.size != 3) return trimmed
    return "${parts[2]}-${parts[1]}-${parts[0]}"
}
